import { Reducer } from 'react'
import { AppThunk as RealAppThunk, RootState as RealRootState } from '../root.reducer'
import { MediaFormats } from '../Common/MediaFormats'
import { User } from '../Permissions/AuthenticatedUser.context'
import { AppAction } from './app.actions'
import { ApplicationPage, CustomTenantTheme, FeatureFlagConfig, Prerequisites, UserLicenses, AppProperties } from './app.api'
import { LOCAL_STORAGE_LAST_USED_THEME_CONFIG } from './app.constants'
import { AuthenticationConfig, WfmConfigC1 } from './LegacyLogin/login.api'
import { TenantSummary } from './Login/loginApi'

export interface ForgotPasswordResult {
  email: string,
  expiration: number,
}
interface AppState {
  authLocation: string,
  loading: boolean,
  ready: boolean,
  initialRoute: string | null,
  routeReady: boolean,
  prerequisites: Prerequisites | null,
  launchDarklyConfig: FeatureFlagConfig | null,
  appProperties: AppProperties | null,
  userLicenses: UserLicenses | null,
  dxStatus: boolean,
  /** @todo both null and false seem to signify the lack of dx access when only false should be used for this purpose */
  dxUserAccess: boolean | null,
  user: User | null,
  /** @todo both null and empty array seem to signify the lack of a teleopti config when only null should be used for this purpose */
  teleoptiConfig: Record<string, string> | null | [],
  loginError: string,
  showSysConfig: boolean,
  singleSignOnError: string,
  tenants: TenantSummary[],
  mediaFormats: MediaFormats,
  /** @todo the partial axios response wrapper causes confusion; find a way around this; move to React context without the wrapper? */
  theme: CustomTenantTheme | null,
  themeLoading: boolean,
  themeError: $TSFIXME,
  isFirstLoad: boolean,
  wfmMenuItems: WfmConfigC1 | null,
  forgotPasswordResult: ForgotPasswordResult | null,
  error: $TSFIXME | null,
  forgerockIdToken: string,
  frLoginUrl: string,
  frClientId: string,
  frOpUrl: string,
  frRealm: string,
  wasAuthFR: boolean,
  landingPage: ApplicationPage | null,
  authConfig: AuthenticationConfig | null,
}

const lastUsedThemeConfig = localStorage.getItem(LOCAL_STORAGE_LAST_USED_THEME_CONFIG)
const initialThemeData = lastUsedThemeConfig ? JSON.parse(lastUsedThemeConfig) : null

const initialForgerockIdToken = window.localStorage.getItem('FORGEROCK_ID_TOKEN') ?? ''
const initialFrOpUrl = window.localStorage.getItem('FR_OP_URL') ?? ''
const initialFrRealm = window.localStorage.getItem('FR_REALM') ?? ''
const initialFrLoginUrl = window.localStorage.getItem('forgerockLoginUrl') ?? ''
const initialAuthLocation = window.localStorage.getItem('authLocation') ?? ''
const initialWasAuthFR = window.localStorage.getItem('wasAuthFR') === 'true'
const initialFrClientId = localStorage.getItem('FR_ClIENT_ID') ?? ''

const initialState: AppState = {
  authLocation: initialAuthLocation,
  loading: false,
  ready: false,
  initialRoute: null,
  routeReady: false,
  prerequisites: null,
  launchDarklyConfig: null,
  appProperties: null,
  userLicenses: null,
  dxStatus: false,
  dxUserAccess: null,
  user: null,
  teleoptiConfig: null,
  loginError: '',
  showSysConfig: false,
  singleSignOnError: '',
  tenants: [],
  mediaFormats: { audio: 'webma', video: 'webmv' },
  theme: initialThemeData ? initialThemeData : null,
  themeLoading: false,
  themeError: null,
  isFirstLoad: false,
  wfmMenuItems: null,
  forgotPasswordResult: null,
  error: null,
  forgerockIdToken: initialForgerockIdToken,
  frLoginUrl: initialFrLoginUrl,
  frClientId: initialFrClientId,
  frOpUrl: initialFrOpUrl,
  frRealm: initialFrRealm,
  wasAuthFR: initialWasAuthFR,
  landingPage: null,
  authConfig: null,
}

export const appReducer: Reducer<AppState, AppAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'APP_LOADING':
      return { ...state, loading: action.loading }
    case 'APP_READY':
      return { ...state, ready: action.ready }
    case 'APP_INITIAL_ROUTE':
      return { ...state, initialRoute: action.initialRoute }
    case 'APP_ROUTE_READY':
      return { ...state, routeReady: action.routeReady }
    case 'GET_PREREQUISITES_SUCCESS':
      return { ...state, prerequisites: action.prerequisites }
    case 'GET_PREREQUISITES_FAILURE':
      // TODO:
      return state
    case 'GET_DX_STATUS_SUCCESS':
      return { ...state, dxStatus: action.status }
    case 'GET_DX_STATUS_FAILURE':
      return { ...state, dxStatus: false }
    case 'GET_DX_USER_ACCESS_SUCCESS':
      return { ...state, dxUserAccess: action.status }
    case 'GET_DX_USER_ACCESS_FAILURE':
      return { ...state, dxUserAccess: false }
    case 'SET_MEDIA_FORMATS':
      return { ...state, mediaFormats: action.data }
    case 'GET_TELEOPTI_CONFIG_SUCCESS':
      return { ...state, teleoptiConfig: action.teleoptiConfig }
    case 'GET_TELEOPTI_CONFIG_FAILURE':
      return { ...state, teleoptiConfig: [] }
    case 'GET_LAUNCHDARKLY_CONFIG_SUCCESS':
      return { ...state, launchDarklyConfig: action.launchDarklyConfig }
    case 'GET_LAUNCHDARKLY_CONFIG_FAILURE':
      return { ...state, launchDarklyConfig: null }
    case 'GET_APP_PROPERTIES_SUCCESS':
      return { ...state, appProperties: action.appProperties }
    case 'GET_APP_PROPERTIES_FAILURE':
      return state
    case 'GET_USER_LICENSE_SUCCESS':
      return { ...state, userLicenses: action.userLicenses }
    case 'GET_USER_LICENSE_FAILURE':
      return { ...state, userLicenses: null }
    case 'GET_THEME_REQUEST':
      return { ...state, themeLoading: true }
    case 'GET_THEME_SUCCESS':
      return { ...state, themeLoading: false, theme: action.theme, themeError: null }
    case 'GET_THEME_FAILURE':
      return { ...state, themeLoading: false, theme: null, themeError: action.error }
    // TODO: move to login reducer
    case 'LOGIN_SUCCESS':
      return { ...state, loginError: '', singleSignOnError: '', user: action.user }
    case 'LOGIN_TENANT_SELECT':
      return { ...state, loginError: '', singleSignOnError: '', tenants: action.tenants }
    case 'LOGIN_FAILURE':
      return {
        ...state,
        singleSignOnError: '',
        loginError: action.error.message,
      }
    case 'SHOW_SYS_CONFIG':
      return {
        ...state,
        showSysConfig: action.show,
      }
    case 'SINGLE_SIGN_ON_FAILURE':
      return {
        ...state,
        loginError: '',
        singleSignOnError: action.error.message,
      }
    case 'LOGOUT_SUCCESS':
      return { ...state, user: null, tenants: [] }
    case 'LOGOUT_FAILURE':
      return state
    case 'SEND_EMAIL_SUCCESS':
      return { ...state, forgotPasswordResult: action.result }
    case 'SEND_EMAIL_FAILURE':
      return { ...state, forgotPasswordResult: action.error }
    case 'SET_TELEOPTI_MENU_ITEMS':
      return { ...state, wfmMenuItems: action.menuItems }
    case 'SET_FR_SESSION_INITIALIZED':
      return { ...state, isFRSessionInitialized: action.isFRSessionInitialized }
    case 'SET_AUTH_LOCATION':
      return { ...state, authLocation: action.authLocation }
    case 'SET_FORGEROCK_ID_TOKEN':
      return { ...state, forgerockIdToken: action.forgerockIdToken }
    case 'SET_FR_LOGIN_URL':
      return { ...state, frLoginUrl: action.frLoginUrl }
    case 'SET_FR_CLIENT_ID':
      return { ...state, frClientId: action.frClientId }
    case 'SET_FR_OP_URL':
      return { ...state, frOpUrl: action.frOpUrl }
    case 'SET_FR_REALM':
      return { ...state, frRealm: action.frRealm }
    case 'SET_WAS_AUTH_FR':
      return { ...state, wasAuthFR: action.wasAuthFR }
    case 'SET_FEDRAMP_COMPLIANCE':
      return { ...state, user: { ...state.user, fedRAMPCompliance: action.fedRAMPCompliance } }
    case 'SET_LANDING_PAGE':
      return { ...state, landingPage: action.landingPage }
    case 'SET_AUTH_CONFIG':
      return { ...state, authConfig: action.authConfig }
    default:
      return state
  }
}

/** @deprecated Change import from `src/App/app.reducer` to `src/configureStore` */
export type RootState = RealRootState

/** @deprecated Change import from `src/App/app.reducer` to `src/configureStore` */
export type AppThunk = RealAppThunk
